import React, { ReactNode, useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { ModelBoundsContext } from './ModelBoundsContext';

// Update the CameraAdjust component to position the camera closer to the model
export function CameraAdjust({ children }: { children: ReactNode }) {
  const { scene, camera, size } = useThree();
  const [modelBounds, setModelBounds] = useState<{ center: THREE.Vector3, size: THREE.Vector3, bottom: number } | null>(null);
  const [cameraPositioned, setCameraPositioned] = useState(false);
  
  useEffect(() => {
    // Small delay to ensure the scene is fully loaded
    const timer = setTimeout(() => {
      if (scene.children.length && !cameraPositioned) {
        try {
          // Create a bounding box for the entire scene
          const box = new THREE.Box3().setFromObject(scene);
          
          // Calculate the size and center of the model
          const center = box.getCenter(new THREE.Vector3());
          const sizeVector = box.getSize(new THREE.Vector3());
          
          // Set model bounds for any child components that need it
          const minY = box.min.y;
          setModelBounds({
            center: center,
            size: sizeVector,
            bottom: minY
          });
          
          // Get the largest dimension to ensure the entire model is visible
          const maxDim = Math.max(sizeVector.x, sizeVector.y, sizeVector.z);
          const fov = (camera as THREE.PerspectiveCamera).fov * (Math.PI / 180);
          
          // Calculate an optimal distance based on model size
          let optimalDistance = Math.abs(maxDim / Math.sin(fov / 2) / 2);
          
          // Use a smaller buffer factor to keep camera closer
          const bufferFactor = 1.2; // Reduced from 1.4 to keep camera closer
          optimalDistance *= bufferFactor;
          
          // For extremely large models, apply more moderate scaling
          if (maxDim > 1000) {
            optimalDistance = Math.sqrt(optimalDistance * 30); // Reduced from 50
          }
          
          // Position camera for a typical slicer angle, using spherical coordinates
          // This ensures the build plate stays horizontal (XY plane) with Z pointing up
          const theta = Math.PI / 4; // 45 degrees around Z axis
          const phi = Math.PI / 6;   // 30 degrees from the XY plane
          
          camera.position.set(
            center.x + optimalDistance * Math.sin(phi) * Math.cos(theta),
            center.y + optimalDistance * Math.sin(phi) * Math.sin(theta),
            center.z + optimalDistance * Math.cos(phi)
          );
          
          // Ensure camera up vector is aligned with Z axis (build plate is XY plane)
          camera.up.set(0, 0, 1);
          
          // Make camera look at center of the model
          camera.lookAt(center);
          
          // Update the orbit controls target if available
          const controls = (camera as any).userData.controls;
          if (controls) {
            controls.target.copy(center);
            controls.update();
          }
          
          // Force camera update
          camera.updateProjectionMatrix();
          
          // Mark camera as positioned to prevent further automatic adjustments
          setCameraPositioned(true);
        } catch (error) {
          console.error("Error adjusting camera:", error);
        }
      }
    }, 100); // Small delay to ensure model is loaded
    
    return () => clearTimeout(timer);
  }, [scene, camera, size, cameraPositioned]);
  
  // Reset camera positioned flag when scene changes significantly (new model)
  useEffect(() => {
    setCameraPositioned(false);
  }, [scene.children.length]);
  
  return (
    <ModelBoundsContext.Provider value={modelBounds || {}}>
      {children}
    </ModelBoundsContext.Provider>
  );
} 